import { Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import FilterTable from "../../../components/table/FilterTable";
import LoaderWrapper from "../../../components/wrapper/LoaderWrapper";
import usePaginationWithSearchParams from "../../../hooks/usePaginationWithSearchParams";
import { useAgreementPayments } from "../../../services/agreementsService";
import TableService from "../../../services/tableService";
import {
    MASS_NOTIFICATIONS_TABLE_NAME,
    useMassNotificationsTableConfig,
} from "./MassNotificationsTableConfig";

export function MassNotificationsTab() {
    const { t } = useTranslation();
    const [dataLocal, setDataLocal] = useState([]);
    const [recordsCount, setRecordsCount] = useState(0);
    const [selectedRowId, setSelectedRowId] = useState();
    const [hiddenColumnsForTables, setHiddenColumnsForTables] = useState([]);
    const [columnsOrdersForTables, setColumnsOrdersForTables] = useState([]);
    const [checkedAll, setCheckedAll] = useState(false);
    const [tableLoading, setTableLoading] = useState(false);

    const {
        resetPageNumber,
        searchParams,
        page: dataPage,
        pageSize: dataPageSize,
        handleChangePageWithSearchParams: handleChangeDataPage,
        handleChangePageSizeWithSearchParams: handleChangeDataPageSize,
    } = usePaginationWithSearchParams();

    const noRservationSearchPatams = new URLSearchParams(searchParams.toString());
    noRservationSearchPatams.set("is_reservation", "false");
    const {
        data: paymentsData,
        isLoading: isPaymentsLoading,
        refetch,
    } = useAgreementPayments(noRservationSearchPatams, {
        select: (data) => {
            const hasOrdering = searchParams.has("ordering");

            if (hasOrdering) {
                return data;
            }

            // Sort payments by payment index across all agreements
            // First, create a map of payment to its index within its agreement
            const paymentIndexMap = new Map();

            // Group payments by agreement to calculate their indices
            const paymentsByAgreement = data.results.reduce((acc, payment) => {
                if (!acc[payment.agrepay_agreement]) {
                    acc[payment.agrepay_agreement] = [];
                }
                acc[payment.agrepay_agreement].push(payment);
                return acc;
            }, {});

            // Calculate index for each payment within its agreement
            Object.values(paymentsByAgreement).forEach((payments) => {
                payments.forEach((payment, index) => {
                    paymentIndexMap.set(payment.id, index + 1);
                });
            });

            // Sort by payment index (all 1's, then all 2's, etc.)
            const sortedResults = [...data.results].sort((a, b) => {
                const indexA = paymentIndexMap.get(a.id) || 0;
                const indexB = paymentIndexMap.get(b.id) || 0;
                return indexA - indexB;
            });

            return {
                ...data,
                results: sortedResults,
            };
        },
    });

    useEffect(() => {
        if (paymentsData?.results) {
            setDataLocal(paymentsData.results);
            setRecordsCount(paymentsData.count);
        }
    }, [paymentsData, dataPage, dataPageSize]);

    const tableFieldsConfig = useMassNotificationsTableConfig();

    const onClickButton = useCallback((e) => {
        e.stopPropagation();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setTableLoading(true);
            await refetch();
            setTableLoading(false);
        };
        fetchData();
    }, [searchParams, refetch]);

    const data = useMemo(
        () =>
            TableService.getPreparedDataForCollapsibleTable(
                tableFieldsConfig,
                dataLocal,
                hiddenColumnsForTables?.[MASS_NOTIFICATIONS_TABLE_NAME] || [],
                columnsOrdersForTables?.[MASS_NOTIFICATIONS_TABLE_NAME] || [],
                onClickButton,
            ),
        [
            tableFieldsConfig,
            dataLocal,
            hiddenColumnsForTables,
            columnsOrdersForTables,
            onClickButton,
        ],
    );

    return (
        <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={12}>
                <LoaderWrapper showLoader={isPaymentsLoading && !dataLocal.length}>
                    <FilterTable
                        // @ts-ignore
                        data={data}
                        countRecords={recordsCount}
                        resetPageNumber={resetPageNumber}
                        selectedItemId={selectedRowId}
                        style={{ maxHeight: "70vh" }}
                        headersConfig={TableService.orderColumns(
                            TableService.hideHiddenColumns(
                                tableFieldsConfig,
                                hiddenColumnsForTables?.[MASS_NOTIFICATIONS_TABLE_NAME] || [],
                            ),
                            columnsOrdersForTables?.[MASS_NOTIFICATIONS_TABLE_NAME] || [],
                        )}
                        showCleanFilterIcon={true}
                        showContextMenu={false}
                        showExportToFileButton={false}
                        showDetailsIcon={false}
                        showCheckbox={false}
                        showRecordCounter={false}
                        checkedAll={checkedAll}
                        page={dataPage}
                        onPageChange={handleChangeDataPage}
                        rowsPerPage={dataPageSize}
                        onRowsPerPageChange={handleChangeDataPageSize}
                        setDataLocal={setDataLocal}
                        setCheckedAll={setCheckedAll}
                        setSelectedRowId={setSelectedRowId}
                        isLoading={tableLoading}
                        showCounterRecords={false}
                    />
                </LoaderWrapper>
            </Grid>
        </Grid>
    );
}
